import SUPERSET_CANVAS_CONFIG from '../../../../../../../Components/SupersetCanvas/superset.canvas.config';
import { DEFAULT_CARD_OPTIONS, normalizeCardOptions } from '../../../../../../../Components/SupersetCanvas/helpers/cardOptions';
import { makeCardId, snapToGrid } from '../../../../../../../Components/SupersetCanvas/helpers/payload';
import {
  SUPERSET_CANVAS_ADD_CARD,
  SUPERSET_CANVAS_CLEAR,
  SUPERSET_CANVAS_MOVE_CARD,
  SUPERSET_CANVAS_PUSH_HISTORY,
  SUPERSET_CANVAS_REMOVE_CARD,
  SUPERSET_CANVAS_RESET_INSTANCE,
  SUPERSET_CANVAS_RESIZE_CARD,
  SUPERSET_CANVAS_SELECT_CARD,
  SUPERSET_CANVAS_SET_CARDS,
  SUPERSET_CANVAS_UNDO,
  SUPERSET_CANVAS_UPDATE_CARD_OPTIONS,
} from '../../../../../actionType';

const { CARD_DEFAULTS, HISTORY_LIMIT } = SUPERSET_CANVAS_CONFIG;

// State is keyed by instanceId so two builders can live on one page
// (or a builder and a preview) without sharing a canvas.
const emptyInstance = { cards: [], selectedCardId: null, history: [] };

const initialState = { instances: {} };

const readInstance = (state, instanceId) => state.instances[instanceId] || emptyInstance;

const writeInstance = (state, instanceId, instance) => ({
  ...state,
  instances: { ...state.instances, [instanceId]: instance },
});

const mapCard = (cards, cardId, patch) => cards.map(card => (card.id === cardId ? { ...card, ...patch } : card));

const SupersetCanvasLayout = (state = initialState, action) => {
  const instanceId = action.payload?.instanceId || 'default';
  const instance = readInstance(state, instanceId);

  switch (action.type) {
    case SUPERSET_CANVAS_ADD_CARD: {
      const { chart, x = 0, y = 0, w = CARD_DEFAULTS.W, h = CARD_DEFAULTS.H } = action.payload;
      const card = {
        id: action.payload.cardId || makeCardId(),
        chart,
        x: snapToGrid(Math.max(0, x)),
        y: snapToGrid(Math.max(0, y)),
        w,
        h,
        options: DEFAULT_CARD_OPTIONS,
      };
      return writeInstance(state, instanceId, { ...instance, cards: [...instance.cards, card], selectedCardId: card.id });
    }

    case SUPERSET_CANVAS_MOVE_CARD: {
      const { cardId, x, y } = action.payload;
      return writeInstance(state, instanceId, {
        ...instance,
        cards: mapCard(instance.cards, cardId, { x: snapToGrid(Math.max(0, x)), y: snapToGrid(Math.max(0, y)) }),
      });
    }

    case SUPERSET_CANVAS_RESIZE_CARD: {
      const { cardId, w, h } = action.payload;
      return writeInstance(state, instanceId, {
        ...instance,
        cards: mapCard(instance.cards, cardId, {
          w: snapToGrid(Math.max(CARD_DEFAULTS.MIN_W, w)),
          h: snapToGrid(Math.max(CARD_DEFAULTS.MIN_H, h)),
        }),
      });
    }

    case SUPERSET_CANVAS_REMOVE_CARD: {
      const { cardId } = action.payload;
      return writeInstance(state, instanceId, {
        ...instance,
        cards: instance.cards.filter(card => card.id !== cardId),
        selectedCardId: instance.selectedCardId === cardId ? null : instance.selectedCardId,
      });
    }

    // Options are edited one section at a time ({ title: { bold: true } }),
    // so merge section-wise rather than replacing the whole object.
    case SUPERSET_CANVAS_UPDATE_CARD_OPTIONS: {
      const { cardId, section, values } = action.payload;
      return writeInstance(state, instanceId, {
        ...instance,
        cards: instance.cards.map(card => {
          if (card.id !== cardId) return card;
          const current = normalizeCardOptions(card.options);
          return {
            ...card,
            options: section ? { ...current, [section]: { ...current[section], ...values } } : normalizeCardOptions({ ...current, ...values }),
          };
        }),
      });
    }

    case SUPERSET_CANVAS_SELECT_CARD:
      return writeInstance(state, instanceId, { ...instance, selectedCardId: action.payload.cardId ?? null });

    case SUPERSET_CANVAS_SET_CARDS:
      return writeInstance(state, instanceId, { ...instance, cards: action.payload.cards || [], selectedCardId: null, history: [] });

    case SUPERSET_CANVAS_CLEAR:
      return writeInstance(state, instanceId, { ...instance, cards: [], selectedCardId: null });

    case SUPERSET_CANVAS_PUSH_HISTORY: {
      const history = [...instance.history, instance.cards];
      return writeInstance(state, instanceId, {
        ...instance,
        history: history.length > HISTORY_LIMIT ? history.slice(history.length - HISTORY_LIMIT) : history,
      });
    }

    case SUPERSET_CANVAS_UNDO: {
      if (!instance.history.length) return state;
      const history = [...instance.history];
      const cards = history.pop();
      return writeInstance(state, instanceId, { ...instance, cards, history, selectedCardId: null });
    }

    case SUPERSET_CANVAS_RESET_INSTANCE: {
      const instances = { ...state.instances };
      delete instances[instanceId];
      return { ...state, instances };
    }

    default:
      return state;
  }
};

export default SupersetCanvasLayout;
